import React, { useLayoutEffect, useRef, useEffect } from 'react';

import { useGSAP } from '@/hooks/useGSAP';

import styles from './ContactSection.module.css';
import { useCursor } from '@/contexts/CursorContext';

// Compute translations at module load time (before any React render)
const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return 'en';
  const lang = navigator.language || navigator.languages?.[0] || 'en';
  return lang.startsWith('fr') ? 'fr' : 'en';
};

const LANG = getBrowserLanguage();

const TRANSLATIONS = {
  en: {
    heading: 'Contact Us',
    writeAt: 'Write at:',
  },
  fr: {
    heading: 'Contactez-nous',
    writeAt: 'Écrivez-nous :',
  },
} as const;

const t = TRANSLATIONS[LANG];

interface ContactSectionProps {
  className?: string;
}

export const ContactSection: React.FC<ContactSectionProps> = ({
  className,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  const { setState } = useCursor();
  const { gsap, ScrollTrigger, ready } = useGSAP();

  const trackContact = (type: 'email') => {
    window.dataLayer?.push({
      event: 'contact_click',
      contact_type: type,
    });
  };

  const onMouseEnter = () => {
    setState('link');
  };

  const onMouseLeave = () => {
    setState('default');
  };

  useLayoutEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    const calculateScale = () => {
      // Reset transform to measure true width
      heading.style.transform = 'none';
      const containerWidth = window.innerWidth;
      const textWidth = heading.scrollWidth;

      // Scale to fit full width with small padding
      const scale = (containerWidth * 0.98) / textWidth;
      heading.style.transform = `scale(${scale})`;

      // Reveal after scaling is calculated
      heading.style.visibility = 'visible';
    };

    // Calculate immediately (useLayoutEffect runs before paint)
    calculateScale();

    // Use ResizeObserver for resize updates
    const resizeObserver = new ResizeObserver(() => {
      calculateScale();
    });

    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, []);

  const infoMail = {
    title: t.writeAt,
    mail: 'hello@lnr.agency',
  };

  const containerFooterRef = React.useRef<HTMLDivElement | null>(null);
  const containerMailRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ready || !gsap || !ScrollTrigger) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerFooterRef.current,
          start: 'top 80%',
          markers: false,
        },
      });

      tl.fromTo(
        containerMailRef.current?.children,
        { y: 100, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 1, stagger: 0.2, ease: 'power3.out' }
      );
    }, containerFooterRef);

    ScrollTrigger.refresh();
    return () => ctx.revert();
  }, [ready, gsap, ScrollTrigger]);

  return (
    <footer
      className={`${styles.contactSection} ${className || ''}`}
      ref={containerFooterRef}
      aria-labelledby="contact-section-heading"
    >
      <div className={styles.infosContainer}>
        <a
          href={`mailto:${infoMail.mail}`}
          className={styles.actionUnderline}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={() => trackContact('email')}
        >
          <div
            className={`${styles.infoItem} textReveal`}
            ref={containerMailRef}
          >
            <p className={styles.title}>{infoMail.title}</p>
            <span className={styles.line}>{infoMail.mail}</span>
          </div>
        </a>
      </div>
      <div className={styles.backgroundBlur}>
        <img
          src={'/assets/rectange_footer.png'}
          alt={'background blur'}
          className={styles.image}
          loading="lazy"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </div>
      <div className={styles.accrocheContainer}>
        <h1
          ref={headingRef}
          id="contact-section-heading"
          style={{ visibility: 'hidden' }}
        >
          {t.heading}
        </h1>
      </div>
    </footer>
  );
};
